import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentCategory, getLocalizedAgentFeatures, formatPriceWithCurrency, Currency } from '../utils/i18n';
import React, { useState, useEffect } from 'react';
import { 
  ShieldAlert, ArrowLeft, ArrowRight, Sparkles, ShieldCheck
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';

interface CyberCenterHubViewProps {
  onLaunchAgent: (target: string) => void;
  onBack: () => void;
  language: 'uk' | 'en' | 'pl';
  currency?: Currency;
  currentUserEmail?: string;
}

export const CYBER_AGENT_IDS = [
  'cyber-kszi-auditor',
  'dsszzi-uav-security-auditor',
  'cyber-iso27001-nis2-manager',
  'cyber-incident-soc-analyst',
  'system-architecture-agent',
  'public-api-integrator',
  'eu-cra-cyber-resilience-auditor'
];

const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <ShieldAlert className={className} />;
  return <IconComponent className={className} />;
};

export default function CyberCenterHubView({
  onLaunchAgent,
  onBack,
  language, currency = 'UAH'}: CyberCenterHubViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';
  const [pricing, setPricing] = useState<Record<string, { price: number; limit: number; durationDays: number; singlePrice: number }>>({});

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await fetch('/api/subscription/pricing');
        if (res.ok) {
          const data = await res.json();
          setPricing(data);
        }
      } catch (err) {
        console.error('Failed to fetch cyber pricing:', err);
      }
    };
    fetchPricing();
  }, []);

  const agents = agentsConfig.filter((a: any) => CYBER_AGENT_IDS.includes(a.id));

  return (
    <div className="space-y-8 animate-fade-in pb-12 font-mono">
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-slate-800 rounded-xl p-4 text-white">
        <button
          onClick={onBack}
          className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{isPl ? '← Powrót do Panelu Głównego' : isUk ? '← Назад до Головного Хабу' : '← Back to Main Hub'}</span>
        </button>

        <div className="flex items-center gap-2">
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-red-500/20 text-red-300 border border-red-400/30 rounded-md">
            {isPl ? '🔒 6 Modułów Cyberbezpieczeństwa & IT' : isUk ? '🔒 6 Спеціалізованих Модулів (IT & Кібербезпека)' : '🔒 6 Cyber & IT Security Modules'}
          </span>
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-cyan-500/20 text-cyan-300 border border-cyan-400/30 rounded-md flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-cyan-400" />
            {isPl ? 'ISO 27001 • NIS2 • SOC • CSIRT • API' : isUk ? 'Open APIs • System Design • КСЗІ • ISO 27001' : 'ISO 27001 • NIS2 • SOC • SIEM'}
          </span>
        </div>
      </div>

      <div className="relative bg-gradient-to-r from-slate-950 via-red-950 to-slate-900 border border-red-500/40 rounded-2xl p-6 sm:p-8 text-white shadow-2xl overflow-hidden">
        <div className="absolute right-0 top-0 w-96 h-96 bg-red-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-3xl space-y-3">
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-red-500/30 text-red-300 border border-red-400/40 rounded">
              {isPl ? 'CYBERBEZPIECZEŃSTWO • SOC • AUDYT NIS2 / ISO 27001 • ARCHITEKTURA IT' : isUk ? 'ЦЕНТР КІБЕРБЕЗПЕКИ, SOC ТА IT-КОМПЛАЄНСУ' : 'CYBERSECURITY, SOC & IT COMPLIANCE'}
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold uppercase tracking-wide text-white">
            {isPl ? '🔒 CENTRUM CYBERBEZPIECZEŃSTWA & IT-COMPLIANCE' : isUk ? '🔒 ЦЕНТР КІБЕРБЕЗПЕКИ & IT-КОМПЛАЄНСУ' : '🔒 CYBERSECURITY & IT COMPLIANCE HUB'}
          </h1>
          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-mono">
            {isPl
              ? 'Wielomodułowy audyt infrastruktury krytycznej, przygotowanie SZBI do certyfikacji ISO 27001 / NIS2, analityka incydentów SOC (SIEM/WAF) oraz integracja bezpiecznych API.'
              : isUk
              ? 'Мультиагентний аудит КСЗІ для об\'єктів критичної інфраструктури (КІІ), підготовка СУІБ до сертифікації ISO 27001 / NIS2 та аналіз системних логів (SIEM/WAF) за стандартами CERT-UA.'
              : 'Multi-agent cybersecurity audit for critical infrastructure, ISO 27001 / NIS2 compliance preparation, SOC/SIEM incident triage, and secure system architecture.'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {agents.map((agent: any) => {
          const agentPricing = pricing[agent.id];
          const priceVal = agentPricing ? agentPricing.price : 490;
          const limitVal = agentPricing ? agentPricing.limit : 30;
          const durationDaysVal = agentPricing ? agentPricing.durationDays : 30;
          const singlePriceVal = agentPricing ? agentPricing.singlePrice : 25;

          return (
            <div
              key={agent.id}
              className="bg-slate-950 border border-red-500/30 hover:border-red-400 rounded-2xl p-6 flex flex-col justify-between gap-6 transition-all hover:scale-[1.01] shadow-xl group"
            >
              <div className="space-y-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-2.5 py-1 text-[9px] font-extrabold uppercase bg-red-500/20 text-red-300 border border-red-400/30 rounded">
                    {getLocalizedAgentCategory(agent, language)}
                  </span>
                  <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-purple-500/20 text-purple-300 border border-purple-400/30 rounded">
                    ✨ {agent.model === 'gemini-3.7-flash' ? 'Gemini 3.7 Flash' : 'Gemini 3.7 Flash'}
                  </span>
                  <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-red-500/20 text-red-300 border border-red-400/30 rounded font-mono">
                    {formatPriceWithCurrency(priceVal, currency)} / {limitVal} {isUk ? (limitVal === 1 ? 'запит' : 'запитів') : isPl ? 'zapytań' : 'req.'} ({durationDaysVal} {isUk ? 'дн.' : isPl ? 'dni' : 'days'})
                  </span>
                  {singlePriceVal > 0 && (
                    <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded font-mono">
                      {isUk ? `Разово: ${formatPriceWithCurrency(singlePriceVal, currency)}` : isPl ? `Pojedynczo: ${formatPriceWithCurrency(singlePriceVal, currency)}` : `Single: ${formatPriceWithCurrency(singlePriceVal, currency)}`}
                    </span>
                  )}
                  <span className="px-2 py-0.5 text-[9px] font-bold text-amber-300 bg-amber-950/60 rounded border border-amber-500/30">
                    {isPl ? '🎁 1. start ZA DARMO' : isUk ? '🎁 1-й запуск БЕЗКОШТОВНО' : '🎁 1st Run FREE'}
                  </span>
                </div>

              <div className="flex items-start gap-4">
                <div className="p-3 bg-red-950/80 border border-red-500/30 rounded-xl text-red-400 shrink-0 group-hover:text-red-300 transition-colors">
                  <AgentIcon name={agent.icon} className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-base font-extrabold text-white uppercase group-hover:text-red-300 transition-colors leading-snug">
                    {getLocalizedAgentName(agent, language)}
                  </h3>
                  <p className="text-xs text-slate-400 mt-1 font-mono leading-relaxed line-clamp-3">
                    {getLocalizedAgentDesc(agent, language)}
                  </p>
                </div>
              </div>

              <div className="space-y-1.5 pt-2 border-t border-slate-800">
                {getLocalizedAgentFeatures(agent, language).map((feat: string, idx: number) => (
                  <div key={idx} className="flex items-center gap-2 text-[11px] text-slate-300 font-mono">
                    <ShieldCheck className="w-3.5 h-3.5 text-red-400 shrink-0" />
                    <span>{feat}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3 pt-4 border-t border-slate-800">
              <button
                onClick={() => onLaunchAgent(agent.id)}
                className="w-full py-3 bg-red-600 hover:bg-red-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-md flex items-center justify-center gap-2 cursor-pointer"
              >
                <span>{isPl ? '🚀 Otwórz Moduł Cyberbezpieczeństwa' : isUk ? '🚀 Відкрити Модуль Кібербезпеки' : '🚀 Launch Cyber Module'}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
}

